import React, { useState } from "react";
import { Button, Menu, Textarea } from "@opendate-app/setlist";

import SampleDataPicker from "./SampleDataPicker";
import { useActionContext } from "./ActionContainer";
import SmsOptOutToggle from "../../../../shared/SmsOptOutToggle";
import { hasOptOut } from "../../../../shared/smsOptOut";

const BODY_INPUT_ID = "campaign-sms-body";

const MERGE_TAGS = [
  "{{fan.first_name}}",
  "{{fan.last_name}}",
  "{{fan.full_name}}",
  "{{fan.phone_number}}",
  "{{fan.email}}",
  "{{event.name}}",
  "{{event.image}}",
  "{{event.url}}",
  "{{event.announce_date}}",
  "{{event.announce_time}}",
  "{{event.announce_date_and_time}}",
  "{{event.announce_time_obj}}",
  "{{event.start_date}}",
  "{{event.start_time}}",
  "{{event.start_date_and_time}}",
  "{{event.start_time_obj}}",
  "{{event.door_date}}",
  "{{event.door_time}}",
  "{{event.door_date_and_time}}",
  "{{event.door_time_obj}}",
  "{{event.sales_start_date}}",
  "{{event.sales_start_time}}",
  "{{event.sales_start_date_and_time}}",
  "{{event.sales_start_time_obj}}",
  "{{venue.name}}",
  "{{venue.address}}",
];

const containerStyle = {
  display: "flex",
  flexWrap: "wrap",
  gap: 1,
  background: "var(--sl-border-secondary)",
  border: "1px solid var(--sl-border-secondary)",
  borderRadius: 12,
  overflow: "hidden",
  fontSize: 14,
  color: "var(--sl-text-secondary)",
};

const columnStyle = {
  flex: "1 1 280px",
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  background: "var(--sl-bg-primary)",
};

const detailRowStyle = {
  borderBottom: "1px solid var(--sl-border-secondary)",
  padding: "10px 16px",
};

const editorStyle = {
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-end",
  gap: 4,
  padding: 12,
};

const previewContainerStyle = {
  display: "flex",
  flexDirection: "column",
  padding: "10px",
  gap: "5px",
};

const characterCountStyle = {
  color: "var(--sl-text-quaternary)",
  fontSize: "12px",
  textAlign: "right",
  width: "100%",
  marginBottom: "10px",
};

export const messageStyle = {
  background: "var(--sl-border-secondary)",
  color: "var(--sl-text-primary)",
  borderRadius: "25px 25px 25px 0px",
  lineHeight: "16px",
  width: "85%",
  padding: "10px 20px",
  wordWrap: "break-word",
  whiteSpace: "pre-wrap",
};

const dataPickerStyle = {
  flex: "1 1 280px",
  minWidth: 0,
  background: "var(--sl-bg-primary)",
  display: "flex",
  flexDirection: "column",
  gap: "15px",
  padding: "15px",
};

const imageContainerStyle = {
  alignItems: "center",
  borderRadius: "25px 25px 25px 25px",
  display: "flex",
  height: "auto",
  justifyContent: "center",
  overflow: "hidden",
  width: "85%",
};

const imageStyle = {
  height: "auto",
  verticalAlign: "middle",
  width: "100%",
};

const SmsTab = () => {
  const {
    action,
    body,
    parsedBody,
    sampleData,
    setBody,
    sampleFanId,
    sampleEventIds,
  } = useActionContext();
  const [cursorPosition, setCursorPosition] = useState(null);

  const handleCursor = (e) => {
    setCursorPosition(e.target.selectionStart);
  };

  const insertTag = (tag) => {
    const text = body || "";
    const optOutBreak = hasOptOut(text) ? text.trimEnd().lastIndexOf("\n") : text.length;
    const position = cursorPosition ?? Math.max(optOutBreak, 0);
    const inserted = cursorPosition === null && optOutBreak === -1 ? `${tag}\n` : tag;
    const caret = position + tag.length;

    setBody(text.substring(0, position) + inserted + text.substring(position));
    setCursorPosition(caret);
    setTimeout(() => {
      const textarea = document.getElementById(BODY_INPUT_ID);
      textarea?.focus();
      textarea?.setSelectionRange(caret, caret);
    }, 0);
  };

  const sampleDataAvailable = !!sampleFanId && !!sampleEventIds.length;

  return (
    <div style={containerStyle}>
      {sampleDataAvailable ? (
        <div style={dataPickerStyle}>
          <SampleDataPicker />
        </div>
      ) : null}
      <div style={columnStyle}>
        <div style={detailRowStyle}><b>From: </b>{action.from}</div>
        <div style={detailRowStyle}><b>To: </b>{"+1{{customer.PhoneNumber}}"}</div>
        <div style={editorStyle}>
          <div style={{ width: "100%" }}>
            <Textarea
              id={BODY_INPUT_ID}
              aria-label="Message"
              rows={10}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Enter your text here..."
              onClick={handleCursor}
              onSelect={handleCursor}
              onKeyUp={handleCursor}
            />
          </div>
          <Menu
            placement="bottom-end"
            minWidth={260}
            trigger={<Button variant="link-color" size="sm">Merge tags</Button>}
            items={MERGE_TAGS.map((tag) => ({ key: tag, label: tag, onClick: () => insertTag(tag) }))}
          />
        </div>
        <SmsOptOutToggle text={body} onChange={setBody} divided />
      </div>
      <div style={columnStyle}>
        <div style={detailRowStyle}><b>From: </b>{action.from}</div>
        <div style={detailRowStyle}><b>To: </b>+1{sampleData.fan.phone_number}</div>
        <div style={previewContainerStyle}>
          <div style={characterCountStyle}>
            {parsedBody.length} characters
          </div>
          <div style={imageContainerStyle}>
            <img src={sampleData.event.image} style={imageStyle} />
          </div>
          <div style={messageStyle}>
            {parsedBody}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SmsTab;
